import {
  jobLabel,
  mentionEmail,
  mentionRecipients,
  type Job,
  type Mentionable,
} from "@roof-hawk/core";

import { sendEmail } from "./gmail";

/**
 * Telling the tagged reps, by email.
 *
 * Jonathon, 2026-08-25: "I want the note tags and replies to go to the reps
 * email as well so we don't miss it." Roof Board is a phone app that people
 * close; email is the thing everybody in this company already watches.
 *
 * It goes out **as the person who wrote the note**, through the same
 * `gmail-send` function every other email in the app uses. There is no
 * server-side mailer here and adding one would be a second way for the company
 * to send mail, with its own domain reputation and its own way of going wrong.
 * The consequence is honest and worth stating: a rep who has not connected
 * their Google account can tag somebody and the tag is recorded, but no email
 * leaves. The panel says so rather than pretending.
 *
 * No `jobId` is passed to the send. That is deliberate — `gmail-send` writes an
 * activity row for anything with a job on it, and a note that logs "Emailed
 * craig@example.com" underneath itself is the same fact twice.
 */
export async function emailNoteMentions(input: {
  /** `contact` is the customer, and the tag says whose roof it is about. */
  job: Pick<Job, "id" | "job_number" | "address" | "contact">;
  /** The note as it was typed, `@name` and all. */
  body: string;
  /** Ids tagged in this note. */
  mentions: readonly string[];
  /** Everybody already in the thread — the author of it, and its repliers. */
  thread?: readonly string[];
  /** Who wrote it; never emailed about their own note. */
  author: string | null;
  authorName: string;
  people: readonly Mentionable[];
  isReply: boolean;
}): Promise<{ sent: string[]; failed: string[] }> {
  const recipients = mentionRecipients({
    mentions: input.mentions,
    thread: input.thread,
    author: input.author,
    people: input.people,
  });

  const letter = mentionEmail({
    author: input.authorName,
    job: jobLabel(input.job),
    body: input.body,
    url: `${window.location.origin}/#job=${input.job.id}`,
    isReply: input.isReply,
  });

  const sent: string[] = [];
  const failed: string[] = [];

  // One at a time, and one failure does not stop the rest: two people tagged
  // and one bad address must not cost the other person their email.
  for (const person of recipients) {
    try {
      await sendEmail({
        to: person.email!.trim(),
        subject: letter.subject,
        text: letter.text,
        internal: true,
        // Staff to staff. The customer-facing signature has no place here.
        sign: false,
      });
      sent.push(person.full_name ?? "them");
    } catch {
      failed.push(person.full_name ?? "them");
    }
  }

  return { sent, failed };
}
